import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  TextField, 
  Box, 
  IconButton 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useAxios from '../../../../hooks/useAxios';
import './UpdateQuestionPage.css'; // Reusing the styling file

function UpdateQuizGradesModal({ open, onClose, questions: initialQuestions, quizId }) {
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const axiosInstance = useAxios();

  useEffect(() => {
    if (open && initialQuestions) {
      // Deep copy to avoid mutating parent state
      const clonedQuestions = JSON.parse(JSON.stringify(initialQuestions));
      setQuestions(clonedQuestions);
      setError(null);
    }
  }, [open, initialQuestions]);

  const handleQuestionGradeChange = (questionIndex, value) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    // Only allow changing grade if no subquestions
    if (!question.subquestions || question.subquestions.length === 0) {
      const grade = parseFloat(value);
      updatedQuestions[questionIndex].grade = isNaN(grade) ? 0 : grade;
      setQuestions(updatedQuestions);
    }
  };

  const handleSubquestionGradeChange = (questionIndex, subquestionIndex, value) => {
    const updatedQuestions = [...questions];
    const subquestions = updatedQuestions[questionIndex].subquestions;
    const grade = parseFloat(value);
    subquestions[subquestionIndex].grade = isNaN(grade) ? 0 : grade;

    // Recalculate the parent question grade as sum of subquestions
    const totalSub = subquestions.reduce((sum, sq) => sum + (sq.grade || 0), 0);
    updatedQuestions[questionIndex].grade = totalSub;
    setQuestions(updatedQuestions);
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate sum of main questions
    const totalMainQuestions = questions.reduce((sum, q) => sum + (q.grade || 0), 0);
    if (totalMainQuestions !== 100) {
      setError('Total grades of main questions must add up to 100.');
      return;
    }

    // Prepare data for request
    const requestData = {
      questions: questions.map(q => {
        let qData = {
          id: q.id,
          grade: q.grade || 0
        };
        if (q.subquestions && q.subquestions.length > 0) {
          qData.subquestions = q.subquestions.map(sq => ({
            id: sq.id,
            grade: sq.grade || 0
          }));
        }
        return qData;
      })
    };

    try {
      const response = await axiosInstance.post(`/quizzes/${quizId}/update-grades`, requestData);
      if (response.data.success) {
        onClose(true); // You can pass a flag to indicate success if needed
      } else {
        setError(response.data.message || 'Failed to update grades.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md">
      <DialogTitle>
        Update Assessment Grades
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography variant="body1" color="error" sx={{ marginBottom: 2 }}>
            {error}
          </Typography>
        )}
        {questions.map((question, qIndex) => (
          <Box key={question.id} sx={{ marginBottom: 3 }}>
            {/* Parent Question */}
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="space-between" 
              sx={{ marginBottom: 1 }}
            >
              <Typography 
                variant="body1" 
                className="choices-title" 
                sx={{ flex: 1, marginRight: 2 }}
              >
                {question.body}
              </Typography>
              {question.subquestions && question.subquestions.length > 0 ? (
                <TextField
                  label="Grade (Sum)"
                  value={question.grade || 0}
                  InputProps={{ readOnly: true }}
                  className="custom-text-field"
                  sx={{ width: '100px' }}
                />
              ) : (
                <TextField
                  label="Grade"
                  value={question.grade || 0}
                  onChange={(e) => handleQuestionGradeChange(qIndex, e.target.value)}
                  type="number"
                  className="custom-text-field"
                  sx={{ width: '100px' }}
                  inputProps={{ min: 0, max: 100 }}
                />
              )}
            </Box>

            {/* Subquestions if any */}
            {question.subquestions && question.subquestions.length > 0 && (
              <Box sx={{ marginLeft: 3 }}>
                {question.subquestions.map((subq, sqIndex) => (
                  <Box 
                    key={subq.id} 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between" 
                    sx={{ marginBottom: 1 }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, marginRight: 2 }}>
                      {subq.body}
                    </Typography>
                    <TextField
                      label="Grade"
                      value={subq.grade || 0}
                      onChange={(e) => handleSubquestionGradeChange(qIndex, sqIndex, e.target.value)}
                      type="number"
                      className="custom-text-field"
                      sx={{ width: '100px' }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} className="secondary-button cancel-button">
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="primary-button submit-button">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UpdateQuizGradesModal;
